// ===================================================================
// HMGP — Map View
// Leaflet map showing HMGP projects and subapplications as markers
// Colored by project type, with legend and popups
// ===================================================================

import { useState, useEffect, useMemo } from 'react';
import { Title, Text, Paper, Group, Stack, Badge, Center, Loader, Alert } from '../../../components/gab/GabComponents';
import { IconMapPin, IconAlertCircle } from '@tabler/icons-react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import {
  HMGP_PROJECT_TYPE_LABELS,
  HMGP_PROJECT_TYPE_COLORS,
  HMGP_STATUS_LABELS,
  type HMGPProjectType,
} from '../../../types/pagrants';

// ── Helpers ─────────────────────────────────────────────────────

function makeDivIcon(color: string, size: number): L.DivIcon {
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${color}" stroke="#fff" stroke-width="1.5" opacity="0.85"/>
    </svg>`,
  });
}

const SUBAPP_ICON = makeDivIcon('#9CA3AF', 10);

function fmtCurrency(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n) || n === 0) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// ── Component ───────────────────────────────────────────────────

export default function HMGPMap() {
  const { projects, subapplications, fetchProjects, fetchSubapplications } = useHMGPStore();
  const { organizations, fetchOrganizations } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchProjects(), fetchSubapplications(), fetchOrganizations()]).finally(() => setLoading(false));
  }, []);

  // Filter by active disaster
  const filteredProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  const filteredSubapps = useMemo(() => {
    if (!activeDisaster) return [];
    return subapplications.filter(s => String(s.disasterId) === String(activeDisaster.Rid));
  }, [subapplications, activeDisaster]);

  // Items with valid coordinates
  const projectMarkers = useMemo(() =>
    filteredProjects.filter(p => parseFloat(p.projectLat) && parseFloat(p.projectLng)),
    [filteredProjects],
  );

  const subappMarkers = useMemo(() =>
    filteredSubapps.filter(s => parseFloat(s.projectLat) && parseFloat(s.projectLng)),
    [filteredSubapps],
  );

  const orgMap = useMemo(() => {
    const m: Record<string, string> = {};
    organizations.forEach(o => { m[String(o.Rid)] = o.orgName; });
    return m;
  }, [organizations]);

  // Unique project types present for legend
  const activeTypes = useMemo(() => {
    const types = new Set<HMGPProjectType>();
    projectMarkers.forEach(p => { if (p.projectType) types.add(p.projectType as HMGPProjectType); });
    return Array.from(types);
  }, [projectMarkers]);

  // Icon cache by type
  const iconCache = useMemo(() => {
    const cache: Record<string, L.DivIcon> = {};
    Object.entries(HMGP_PROJECT_TYPE_COLORS).forEach(([type, color]) => {
      cache[type] = makeDivIcon(color, 16);
    });
    cache['_default'] = makeDivIcon('#059669', 16);
    return cache;
  }, []);

  if (loading) return <Center h={200}><Loader /></Center>;

  if (!activeDisaster) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Map</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="#C68700" title="No Active Disaster">
          Select an active incident in the top bar to view HMGP project locations.
        </Alert>
      </Stack>
    );
  }

  const hasData = projectMarkers.length > 0 || subappMarkers.length > 0;

  return (
    <Stack p="md" gap="sm">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Map</Title>
          <Text size="sm" c="dimmed">
            {filteredProjects.length} projects, {filteredSubapps.length} subapplications
          </Text>
        </div>
        <Badge color="var(--color-success)" variant="light" leftSection={<IconMapPin size={12} />}>
          {projectMarkers.length + subappMarkers.length} mapped
        </Badge>
      </Group>

      {!hasData && (
        <Alert icon={<IconAlertCircle size={16} />} color="#939598" title="No Mapped Data">
          No HMGP projects or subapplications have coordinates set. Add latitude/longitude to projects to see them on the map.
        </Alert>
      )}

      <Paper withBorder style={{ borderRadius: 4, overflow: 'hidden' }}>
        <MapContainer
          center={[39.8, -98.5]}
          zoom={4}
          style={{ height: 'calc(100vh - 200px)', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Project markers */}
          {projectMarkers.map(p => (
            <Marker
              key={`proj-${p.Rid}`}
              position={[parseFloat(p.projectLat), parseFloat(p.projectLng)]}
              icon={iconCache[p.projectType] || iconCache['_default']}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <strong>{p.projectTitle || p.projectNumber}</strong>
                  <br />
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType}
                  </span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Status: {HMGP_STATUS_LABELS[p.status as keyof typeof HMGP_STATUS_LABELS] || p.status}
                  </span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Estimated Cost: {fmtCurrency(p.estimatedCost)}
                  </span>
                  {p.organizationId && (
                    <>
                      <br />
                      <span style={{ fontSize: 12, color: '#888' }}>
                        {orgMap[p.organizationId] || ''}
                      </span>
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Subapplication markers (smaller, gray) */}
          {subappMarkers.map(s => (
            <Marker
              key={`sub-${s.Rid}`}
              position={[parseFloat(s.projectLat), parseFloat(s.projectLng)]}
              icon={SUBAPP_ICON}
            >
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <strong>{s.subappTitle || s.subappNumber}</strong>
                  <br />
                  <span style={{ fontSize: 12, color: '#666' }}>
                    Subapplication &middot; {HMGP_PROJECT_TYPE_LABELS[s.projectType as HMGPProjectType] || s.projectType}
                  </span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Status: {s.status}
                  </span>
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Estimated Cost: {fmtCurrency(s.estimatedCost)}
                  </span>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Paper>

      {/* Legend */}
      {activeTypes.length > 0 && (
        <Paper p="sm" withBorder>
          <Text size="sm" fw={600} mb={4}>Legend &mdash; Project Types</Text>
          <Group gap="md" wrap="wrap">
            {activeTypes.map(type => (
              <Group key={type} gap={6}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: HMGP_PROJECT_TYPE_COLORS[type] || '#059669',
                  border: '1px solid #ccc',
                }} />
                <Text size="xs">{HMGP_PROJECT_TYPE_LABELS[type] || type}</Text>
              </Group>
            ))}
            <Group gap={6}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: '#9CA3AF',
                border: '1px solid #ccc',
              }} />
              <Text size="xs" c="dimmed">Subapplication</Text>
            </Group>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
